import { jwtDecode } from 'jwt-decode';
import { toast } from 'sonner';

import { logout, userManager } from '@/lib/auth';
import type { CRUDResponse } from '@/lib/data';
import { accountRestStorage } from '@/lib/data/domains/account/rest-storage';
import { generateApiErrorToastContent } from '@/lib/utils';
import {
  classifyRequestFailure,
  createRequestReference,
  REQUEST_ID_HEADER,
  REQUEST_TIMEOUT_MS,
  RETRY_DELAY_MS,
  shouldRetry,
} from '@/lib/utils/request-failure';
import { useAccessDeniedStore } from '@/store/access-denied';
import { useAuthStore } from '@/store/auth';
import { useImpersonationStore } from '@/store/impersonation';

/**
 * Advisory headers the API sends when the caller's permissions changed.
 * The No-*-Permission-Version headers are deliberately not listed.
 */
const PERMISSION_VERSION_HEADERS = [
  'User-Permissions-Obsolete',
  'User-Permissions-Unconfirmed',
  'Application-Permissions-Obsolete',
  'Application-Permissions-Unconfirmed',
];

// At most one header-triggered refresh per window
const PERMISSIONS_REFRESH_DEBOUNCE_MS = 30000;

// Give up waiting for another tab's refresh after this long
const EXTERNAL_REFRESH_TIMEOUT_MS = 15000;

// Renew failures that a trip through Logto's consent step can fix
const CONSENT_RENEW_ERRORS = ['consent_required', 'interaction_required'];

/**
 * Rest Client Service
 */
export class RestClientService {
  private _currentToken: string | null = null;

  // ✅ same-tab lock
  private _refreshPromise: Promise<boolean> | null = null;

  // ✅ cross-tab signal
  private _channel = new BroadcastChannel('auth_channel');
  private _isRefreshingExternally = false;

  // start time of the last header-triggered refresh (debounce)
  private _lastPermissionsRefreshAt: number | null = null;

  // token the auth store's user was last refetched for
  private _userFetchedToken: string | null = null;

  // a consent redirect is already under way
  private _consentRedirect: Promise<boolean> | null = null;

  // bumped whenever the token is set from outside (impersonation, login)
  private _tokenEpoch = 0;

  // token the last request reported to the denied state carried
  private _forbiddenToken: string | null = null;

  constructor() {
    // optional chaining: sibling tests mock userManager without events
    userManager.events?.addSilentRenewError?.((error) => {
      void this.redirectForConsent(error);
    });

    this._channel.onmessage = async (event) => {
      if (event.data?.type === 'REFRESH_START') {
        this._isRefreshingExternally = true;
      }

      if (event.data?.type === 'REFRESH_DONE') {
        this._isRefreshingExternally = false;

        // the impersonated token lives only here; the admin's would replace it
        if (useImpersonationStore.getState().isImpersonating) return;

        // sync latest token immediately after another tab finishes refresh
        // (this handler owns the refetch for every refresh another tab runs)
        const previousToken = this._currentToken;
        await this.syncTokenFromUserManager();

        if (this._currentToken !== previousToken) {
          await this.refetchUser();
        }
      }
    };
  }

  public getCurrentToken(): string | null {
    return this._currentToken;
  }

  public setCurrentToken(token: string | null): void {
    this._tokenEpoch += 1;
    this._currentToken = token;
  }

  public getUserFetchedToken(): string | null {
    return this._userFetchedToken;
  }

  /**
   * Refetch /account/me into the auth store without the full-page loader.
   * The result is dropped when the token changed or impersonation started
   * while the request was in flight.
   */
  public async refetchUser(): Promise<void> {
    const token = this._currentToken;

    if (!token || useImpersonationStore.getState().isImpersonating) return;

    try {
      const response = await accountRestStorage.fetchUser({});

      if (
        this._currentToken !== token ||
        useImpersonationStore.getState().isImpersonating ||
        !response.data
      ) {
        return;
      }

      useAuthStore.getState().setUser(response.data);
      this._userFetchedToken = token;
    } catch (error) {
      console.error('Refetch user error:', error);
    }
  }

  /**
   * Whether a failed renew can never succeed, so only a sign-out helps: an
   * OAuth error code (invalid_grant, login_required, ...), or no stored
   * refresh token (oidc-client-ts then reports a missing silent_redirect_uri).
   * Duck-typed like redirectForConsent. A network error, timeout or a 5xx
   * without an OAuth code is transient.
   */
  private isDefinitiveRefreshFailure(error: unknown): boolean {
    const code = (error as { error?: unknown } | null)?.error;

    if (typeof code === 'string' && code !== '') return true;

    return (
      error instanceof Error &&
      error.message === 'No silent_redirect_uri configured'
    );
  }

  /**
   * Sends the user through Logto's consent step and back to the current page
   * when a failed renew says the grant lacks consent. Resolves true only when
   * the redirect started; false, without touching anything, for every other
   * error, and false when the redirect itself failed to start (the caller
   * then treats the renew as failed). Never rejects.
   */
  private async redirectForConsent(error: unknown): Promise<boolean> {
    const code = (error as { error?: unknown } | null)?.error;

    if (typeof code !== 'string' || !CONSENT_RENEW_ERRORS.includes(code)) {
      return false;
    }

    // a caller joining a redirect in flight shares its outcome: if it fails
    // to start, that caller can still fall back to logout
    if (this._consentRedirect) {
      return this._consentRedirect;
    }

    const redirect = (async () => {
      try {
        await userManager.signinRedirect({
          prompt: 'consent',
          state: {
            returnTo: window.location.pathname + window.location.search,
          },
        });

        return true;
      } catch (redirectError) {
        console.error('Consent redirect error:', redirectError);

        return false;
      }
    })();

    this._consentRedirect = redirect;

    const started = await redirect;

    // a redirect that started stays shared: the page is navigating away
    if (!started) this._consentRedirect = null;

    return started;
  }

  /**
   * Fetch latest token from userManager
   */
  private async syncTokenFromUserManager(): Promise<void> {
    const user = await userManager.getUser();

    if (!user) {
      logout();
      return;
    }

    this._currentToken = user.access_token as string;
  }

  /**
   * Wait for other tab refresh to finish
   */
  private async waitForExternalRefresh(): Promise<void> {
    if (!this._isRefreshingExternally) return;

    return new Promise((resolve) => {
      // the other tab may have died mid-refresh; do not hang the request
      const timer = setTimeout(() => {
        this._channel.removeEventListener('message', handler);
        this._isRefreshingExternally = false;
        resolve();
      }, EXTERNAL_REFRESH_TIMEOUT_MS);

      const handler = async (event: MessageEvent) => {
        if (event.data?.type === 'REFRESH_DONE') {
          clearTimeout(timer);
          this._channel.removeEventListener('message', handler);

          if (!useImpersonationStore.getState().isImpersonating) {
            await this.syncTokenFromUserManager();
          }

          resolve();
        }
      };

      this._channel.addEventListener('message', handler);
    });
  }

  /**
   * Checks if the JWT token is expired
   */
  public isTokenExpired(): boolean {
    if (!this._currentToken) return true;

    const decodedToken: any = jwtDecode(this._currentToken);
    const currentTime = Date.now() / 1000; // Current time in seconds (UNIX timestamp)

    return decodedToken.exp < currentTime; // Check if the token is expired
  }

  /**
   * Refresh token (same-tab + multi-tab safe)
   */
  public async refreshToken(): Promise<boolean> {
    // ✅ same-tab protection
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    // ✅ cross-tab wait
    if (this._isRefreshingExternally) {
      await this.waitForExternalRefresh();
      return true;
    }

    const epoch = this._tokenEpoch;

    this._refreshPromise = (async () => {
      try {
        // 🔔 notify other tabs
        this._channel.postMessage({ type: 'REFRESH_START' });

        // signinSilent handles token refresh internally
        await userManager.signinSilent();

        // sync latest token after refresh, unless the token was set from
        // outside meanwhile (impersonation start/stop, re-login): the
        // renewed one would be stale for the new session
        if (this._tokenEpoch === epoch) {
          await this.syncTokenFromUserManager();
        }

        return true;
      } catch (error) {
        if (await this.redirectForConsent(error)) return false;

        if (this.isDefinitiveRefreshFailure(error)) {
          console.error('Refresh token error:', error);
          logout();
        } else {
          // network error, timeout or a Logto 5xx: the refresh token may
          // still be good, so keep the session; the next request retries
          console.warn('Refresh token failed, keeping the session:', error);
        }

        return false;
      } finally {
        this._refreshPromise = null;

        // 🔔 notify others
        this._channel.postMessage({ type: 'REFRESH_DONE' });
      }
    })();

    return this._refreshPromise;
  }

  private headersRequestRefresh(headers: Headers): boolean {
    return PERMISSION_VERSION_HEADERS.some((name) => headers.has(name));
  }

  /**
   * Silent refresh after the API flagged the permissions as changed.
   * Debounced, and never while impersonating (a refresh would replace the
   * impersonated token with the admin's).
   */
  public async refreshPermissions(): Promise<void> {
    if (useImpersonationStore.getState().isImpersonating) return;

    const now = Date.now();

    if (
      this._lastPermissionsRefreshAt !== null &&
      now - this._lastPermissionsRefreshAt < PERMISSIONS_REFRESH_DEBOUNCE_MS
    ) {
      return;
    }

    this._lastPermissionsRefreshAt = now;

    const previousToken = this._currentToken;

    // joining another tab's refresh: the channel handler refetches for it
    const joinsExternalRefresh =
      !this._refreshPromise && this._isRefreshingExternally;

    await this.refreshToken();

    if (!joinsExternalRefresh && this._currentToken !== previousToken) {
      await this.refetchUser();
    }
  }

  /**
   * One renewal attempt for a page the API refused: unlike a header-triggered
   * refresh it waits for the debounce window instead of returning at once.
   * Resolves whether the token changed. A token already renewed since the
   * refused request resolves true at once, without waiting or renewing.
   * Aborting `signal` while it waits, or before the renewal starts, resolves
   * false and renews nothing; a renewal already running is not interrupted.
   */
  public async retryAfterForbidden(signal?: AbortSignal): Promise<boolean> {
    if (signal?.aborted) return false;

    // a refresh still running may already bring the renewed token
    if (this._refreshPromise) await this._refreshPromise;
    if (this._isRefreshingExternally) await this.waitForExternalRefresh();

    if (signal?.aborted) return false;

    if (
      this._forbiddenToken !== null &&
      this._currentToken !== this._forbiddenToken
    ) {
      return true;
    }

    if (this._lastPermissionsRefreshAt !== null) {
      const remaining =
        PERMISSIONS_REFRESH_DEBOUNCE_MS -
        (Date.now() - this._lastPermissionsRefreshAt);

      if (remaining > 0) {
        const waited = await new Promise<boolean>((resolve) => {
          const onAbort = () => {
            clearTimeout(timer);
            resolve(false);
          };
          const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve(true);
          }, remaining);

          signal?.addEventListener('abort', onAbort, { once: true });
        });

        if (!waited) return false;
      }
    }

    if (signal?.aborted) return false;

    const sentToken = this._currentToken;

    await this.refreshPermissions();

    // a debounced call returns at once: wait for a refresh still running
    if (this._refreshPromise) await this._refreshPromise;
    if (this._isRefreshingExternally) await this.waitForExternalRefresh();

    return this._currentToken !== sentToken;
  }

  /**
   * Makes API request with auto refresh + retry
   * `retry: false` (FMICE-812) opts a request out of the one automatic retry
   * after a no-response failure (slow dashboard aggregates: a 30 s timeout
   * is not re-sent); the default keeps the retry.
   */
  public async request({
    url,
    method,
    body,
    requestInit,
    showErrorToast = true,
    retry = true,
  }: {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: any;
    requestInit?: RequestInit;
    showErrorToast?: boolean;
    retry?: boolean;
  }): Promise<CRUDResponse<any>> {
    // the page that asked: a refusal is judged against it when the answer lands
    const dispatchPath = window.location.pathname;

    if (import.meta.env.VITE_USE_MOCK_DATA === 'true') {
      const { handleMockRequest } = await import('@/lib/mock');
      return handleMockRequest({ url, method, body, requestInit, showErrorToast });
    }

    // one reference per call: every fetch it makes (401 and 403 replays too) carries it
    const reference = createRequestReference();

    // ✅ wait if another tab is refreshing
    if (this._isRefreshingExternally) {
      await this.waitForExternalRefresh();
    }

    // ✅ refresh if expired
    if (this._currentToken && this.isTokenExpired()) {
      const refreshed = await this.refreshToken();

      if (!refreshed) {
        return Promise.reject({
          success: false,
          error: 'Token refresh failed',
          reference,
        });
      }
    }

    // token the latest fetch carried in Authorization
    let carriedToken: string | null = null;

    // state of the latest fetch attempt, for classifying how it failed
    let timedOut = false;
    let fetchError: unknown;

    // ends the latest attempt's timeout and caller-signal listener; they stay
    // attached until the response body is consumed or the request fails
    let releaseAttempt: () => void = () => {};

    const makeRequest = async () => {
      releaseAttempt();
      timedOut = false;
      fetchError = undefined;

      let finalUrl = url;
      const token = this._currentToken;
      carriedToken = token;

      if ((method ?? 'GET') === 'GET') {
        const separator = finalUrl.includes('?') ? '&' : '?';
        finalUrl = `${finalUrl}${separator}current=${Date.now()}`;
      }

      // one controller per attempt: the 30 s timeout and a caller's signal both abort it
      const controller = new AbortController();
      const callerSignal = requestInit?.signal;
      const onCallerAbort = () => controller.abort();

      if (callerSignal?.aborted) {
        controller.abort();
      } else {
        callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
      }

      const requestOptions: RequestInit = {
        ...requestInit,
        signal: controller.signal,
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(requestInit?.headers || {}),
          Authorization: token ? `Bearer ${token}` : '',
          [REQUEST_ID_HEADER]: reference,
        },
        body: body
          ? (requestInit?.headers as any)?.['Content-Type'] ===
            'multipart/form-data'
            ? body
            : JSON.stringify(body)
          : undefined,
      };
      // If Content-Type is multipart/form-data, remove it from headers
      if (
        (requestOptions.headers as any)?.['Content-Type'] ===
        'multipart/form-data'
      ) {
        delete (requestOptions.headers as any)['Content-Type'];
      }

      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      releaseAttempt = () => {
        clearTimeout(timer);
        callerSignal?.removeEventListener('abort', onCallerAbort);
      };

      try {
        return await fetch(
          `${import.meta.env.VITE_API_BASE_URL}${finalUrl}`,
          requestOptions,
        );
      } catch (error) {
        fetchError = error;
        releaseAttempt();
        throw error;
      }
    };

    // a body read that fails (timeout, abort, dropped connection) is classified
    // like a fetch that failed
    const readBody = async <T,>(read: () => Promise<T>): Promise<T> => {
      try {
        return await read();
      } catch (error) {
        fetchError = error;
        throw error;
      }
    };

    // wait before the one automatic retry; the caller's signal still aborts it
    const waitToRetry = () =>
      new Promise<void>((resolve, reject) => {
        const signal = requestInit?.signal;
        const aborted = () => new DOMException('Aborted', 'AbortError');

        if (signal?.aborted) {
          reject(aborted());
          return;
        }

        const onAbort = () => {
          clearTimeout(timer);
          reject(aborted());
        };
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', onAbort);
          resolve();
        }, RETRY_DELAY_MS);

        signal?.addEventListener('abort', onAbort, { once: true });
      });

    try {
      let response: Response;

      try {
        response = await makeRequest();
      } catch (firstError) {
        // no response arrived: reads and the drawing save get one more try
        const noResponse =
          fetchError !== undefined &&
          firstError === fetchError &&
          (timedOut || !requestInit?.signal?.aborted);

        if (
          !retry ||
          !noResponse ||
          !shouldRetry(
            method,
            url,
            classifyRequestFailure({
              timedOut,
              online:
                typeof navigator === 'undefined' || navigator.onLine !== false,
              error: firstError,
            }),
          )
        ) {
          throw firstError;
        }

        await waitToRetry();
        response = await makeRequest();
      }

      // 🔥 retry once on 401
      if (response.status === 401) {
        const refreshed = await this.refreshToken();

        // refreshToken() already logged out when the renew failed for good
        if (!refreshed) throw new Error('Unauthorized');

        response = await makeRequest();
      }

      // permissions changed on the API side: renew the token
      if (this.headersRequestRefresh(response.headers)) {
        if (
          response.status === 403 &&
          !useImpersonationStore.getState().isImpersonating
        ) {
          // 🔥 retry once on a flagged 403, only with a renewed token
          const refusedToken = carriedToken;
          await this.refreshPermissions();

          // a debounced call returns at once: wait for a refresh still running
          if (this._refreshPromise) await this._refreshPromise;
          if (this._isRefreshingExternally) await this.waitForExternalRefresh();

          // an unchanged token (compared with the one the refused request carried)
          // would be refused again: let the 403 fail normally
          if (this._currentToken !== refusedToken) {
            response = await makeRequest();
          }
        } else {
          void this.refreshPermissions();
        }
      }

      if (response.status === 403) {
        console.warn(`Access denied: ${method ?? 'GET'} ${url} (403)`);
      }

      // the page that asked is gone or the token was replaced meanwhile: the
      // answer is stale, reject it without a toast or a denied state
      if (
        response.status === 403 &&
        (window.location.pathname !== dispatchPath ||
          this._currentToken !== carriedToken)
      ) {
        throw {
          data: null,
          status: 403,
          statusText: response.statusText,
          success: false,
          error: 'Forbidden',
          reference,
        };
      }

      // the page's own resource is refused: PermissionGuard shows the denied state
      // (a 403 has an empty body, so this comes before the JSON step)
      if (
        response.status === 403 &&
        useAccessDeniedStore.getState().reportForbidden(url)
      ) {
        this._forbiddenToken = carriedToken;

        throw {
          data: null,
          status: 403,
          statusText: response.statusText,
          success: false,
          error: 'Forbidden',
          reference,
        };
      }

      // any other 403 (a download included): say so plainly, with the API's reason when it sent one
      if (response.status === 403) {
        const text = await readBody(() => response.text());
        let data: any = null;

        if (text.trim()) {
          try {
            data = JSON.parse(text);
          } catch {
            data = null;
          }
        }

        const rejection = {
          data,
          status: 403,
          statusText: response.statusText,
          success: false,
          error: 'Forbidden',
          reference,
          accessDenied: true,
        };

        if (showErrorToast) {
          toast.error(generateApiErrorToastContent(rejection));
        }

        throw rejection;
      }

      // handle blob
      if (
        (requestInit?.headers as any)?.['Content-Type'] ===
        'application/octet-stream'
      ) {
        if (response.ok) {
          const blob = await readBody(() => response.blob());
          const url = window.URL.createObjectURL(blob);

          return {
            data: url,
            status: response.status,
            statusText: response.statusText,
            success: true,
          };
        }

        // a 5xx has its own message and reference; anything else keeps the generic text
        if (response.status >= 500) {
          const failure = {
            kind: classifyRequestFailure({
              timedOut: false,
              online: true,
              status: response.status,
            }),
            status: response.status,
          };
          const rejection = {
            data: null,
            status: response.status,
            statusText: response.statusText,
            success: false,
            error: 'Request failed',
            reference,
            failure,
          };

          if (showErrorToast) {
            toast.error(generateApiErrorToastContent(rejection));
          }

          throw rejection;
        }

        if (showErrorToast) {
          toast.error('An error has occurred. Please try again.');
        }

        throw new Error('Request failed');
      }

      let json: any = {};

      if (response.status !== 204) {
        // a 5xx often has an empty or non-JSON body: tolerate that for 5xx only;
        // every other status parses strictly, as before
        const text = await readBody(() => response.text());

        if (response.ok && text.trim() === '') {
          // an empty body on any 2xx is a success with no data
          json = null;
        } else if (response.status >= 500 && !response.ok) {
          try {
            json = text ? JSON.parse(text) : {};
          } catch {
            json = {};
          }
        } else {
          json = JSON.parse(text);
        }
      }

      if (response.ok) {
        return {
          data: json,
          status: response.status,
          statusText: response.statusText,
          success: true,
        };
      }

      const rejection = {
        data: json,
        status: response.status,
        statusText: response.statusText,
        success: false,
        error:
          json?.title || json?.detail
            ? `${json?.title ?? ''}${json?.detail ? ` ${json.detail}` : ''}`
            : 'Request failed',
        reference,
        failure: {
          kind: classifyRequestFailure({
            timedOut: false,
            online: true,
            status: response.status,
            hasDetail: Boolean(
              json?.title ||
              json?.detail ||
              Object.keys(json?.errors || {}).length > 0,
            ),
          }),
          status: response.status,
        },
      };

      if (showErrorToast) {
        // a server failure shows the plain message and the reference; the rest is as before
        toast.error(
          generateApiErrorToastContent(
            rejection.failure.kind === 'server' && !json?.description
              ? rejection
              : json,
          ),
        );
      }

      throw rejection;
    } catch (error: any) {
      // fetch itself rejected (no response): say why, unless the caller aborted it
      if (
        fetchError !== undefined &&
        error === fetchError &&
        (timedOut || !requestInit?.signal?.aborted)
      ) {
        const kind = classifyRequestFailure({
          timedOut,
          online:
            typeof navigator === 'undefined' || navigator.onLine !== false,
          error,
        });
        const decoration = {
          success: false,
          error: timedOut
            ? 'Request timed out'
            : error?.message || 'Request failed',
          reference,
          failure: { kind },
        };

        // decorate the original error in place so callers holding it still match
        const failed =
          error !== null && typeof error === 'object'
            ? Object.assign(error, decoration)
            : decoration;

        if (showErrorToast) {
          toast.error(generateApiErrorToastContent(failed));
        }

        return Promise.reject(failed);
      }

      // the caller cancelled (during the fetch or the retry wait): reject as it did, say nothing
      if (requestInit?.signal?.aborted && error?.name === 'AbortError') {
        return Promise.reject(error);
      }

      if (showErrorToast && !error?.status) {
        toast.error(generateApiErrorToastContent(error));
      }

      return Promise.reject(error);
    } finally {
      releaseAttempt();
    }
  }
}

/**
 * Singleton
 */
export const restService = new RestClientService();
